/**
 * Single source of truth for embeddable ServiceFlow widget types.
 * API validation, web UI, and MCP discovery should all read from this catalog.
 */
import { buildWidgetContainerClassList, buildWidgetIframeClassList, getWidgetEmbedIframeId, getWidgetEmbedContainerId, getWidgetEmbedSelectors, } from './widgetEmbedSelectors.js';
export const WIDGET_CATALOG = [
    {
        type: 'lead',
        label: 'Lead Capture Form',
        description: 'Collect name, email, phone, and message from website visitors.',
        defaultHeight: '600px',
        settingsKey: 'lead_settings',
    },
    {
        type: 'pricebook',
        label: 'Service Catalog',
        description: 'Browse services with pricing, search, and optional booking.',
        defaultHeight: '800px',
        settingsKey: 'price_book_settings',
        embedNotes: 'Includes a postMessage resize listener for dynamic height.',
    },
    {
        type: 'visualizer',
        label: 'Project Visualizer',
        description: '3D / garage-door style project visualizer embed.',
        defaultHeight: '600px',
        settingsKey: 'visualizer_settings',
    },
    {
        type: 'attribute_search',
        label: 'Attribute Search (E-Commerce)',
        description: 'Filterable product/attribute search embed for e-commerce catalogs.',
        defaultHeight: '600px',
        settingsKey: 'attribute_search_settings',
    },
    {
        type: 'reviews',
        label: 'Customer Reviews',
        description: 'Display customer reviews on the website.',
        defaultHeight: '500px',
        settingsKey: 'reviews_settings',
    },
    {
        type: 'quote',
        label: 'Quote Engine',
        description: 'Customer garage-door quote with photo upload and AI door preview.',
        defaultHeight: '920px',
        settingsKey: 'quote_settings',
        embedNotes: 'Same public quote page as the sendable customer link.',
    },
];
const catalogByType = new Map(WIDGET_CATALOG.map((entry) => [entry.type, entry]));
export function listWidgetTypes() {
    return [...WIDGET_CATALOG];
}
export function isValidWidgetType(type) {
    return catalogByType.has(type);
}
export function getWidgetTypeMeta(type) {
    return catalogByType.get(type) ?? null;
}
export function getWidgetTypeLabels() {
    return WIDGET_CATALOG.map((entry) => ({ value: entry.type, label: entry.label }));
}
export const DEFAULT_WIDGET_BASE_URL = 'https://widgets.serviceflow-pro.com';
export function buildWidgetEmbedCode(options) {
    const meta = getWidgetTypeMeta(options.type);
    const height = options.height || meta?.defaultHeight || '600px';
    const width = options.width || '100%';
    const responsive = options.responsive !== false;
    const label = meta?.label || options.name || 'Widget';
    const base = (options.widgetBaseUrl || DEFAULT_WIDGET_BASE_URL).replace(/\/$/, '');
    const theme = options.theme || 'light';
    const params = new URLSearchParams({
        widget: options.widgetId,
        theme,
        ...(options.customParams || {}),
    });
    const embedUrl = `${base}/widget/embed?${params.toString()}`;
    const selectors = getWidgetEmbedSelectors(options.widgetId, options.type, theme);
    const containerId = getWidgetEmbedContainerId(options.widgetId);
    const iframeId = getWidgetEmbedIframeId(options.widgetId);
    const containerClass = buildWidgetContainerClassList(options.type, options.containerClass);
    const iframeClass = buildWidgetIframeClassList(options.iframeClass);
    const title = (options.name || label).replace(/"/g, '&quot;');
    const responsiveStyle = responsive ? 'max-width: 100%;' : '';
    const iframeExtras = options.iframeExtraAttributes ? `\n    ${options.iframeExtraAttributes.trim()}` : '';
    const embedShell = `<!-- ServiceFlow ${label} Widget -->
<div
  id="${containerId}"
  class="${containerClass}"
  data-sf-widget-id="${options.widgetId}"
  data-sf-widget-type="${selectors.dataAttributes.sfWidgetType}"
  data-sf-theme="${theme}"
  style="width: ${width}; height: ${height}; ${responsiveStyle}"
>
  <iframe
    id="${iframeId}"
    class="${iframeClass}"
    src="${embedUrl}"
    width="100%"
    height="100%"
    frameborder="0"
    scrolling="yes"
    allowfullscreen${iframeExtras}
    title="${title}">
  </iframe>
</div>`;
    const includeResize = options.includePricebookResizeListener !== false && options.type === 'pricebook';
    const resizeListener = includeResize
        ? `
<script>
  (function () {
    var containerId = '${containerId}';
    window.addEventListener('message', function (event) {
      var data = event.data;
      if (!data || data.type !== 'serviceflow_widget_resize' || data.widgetId !== '${options.widgetId}') return;
      var container = document.getElementById(containerId);
      var iframe = container && container.querySelector('iframe');
      if (!container || !iframe || !data.height) return;
      var nextHeight = Math.max(Number(data.height), data.modalOpen ? 920 : 640) + 'px';
      container.style.height = nextHeight;
      iframe.style.height = nextHeight;
    });
  })();
</script>`
        : '';
    const embedCode = `${embedShell}${resizeListener}`;
    const wordpressShortcode = `[serviceflow_widget widget="${options.widgetId}" type="${options.type}" width="${width}" height="${height}" theme="${theme}"]`;
    return { embedCode, wordpressShortcode, embedUrl, height, selectors };
}
export function buildWidgetServiceSdkScript(widgetId, embedOrigin) {
    const containerId = getWidgetEmbedContainerId(widgetId);
    const iframeId = getWidgetEmbedIframeId(widgetId);
    const safeOrigin = embedOrigin.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
    return `
<script>
  (function() {
    window.ServiceFlowWidget = window.ServiceFlowWidget || {};
    window.ServiceFlowWidget['${widgetId}'] = {
      resize: function(width, height) {
        const container = document.getElementById('${containerId}');
        if (container) {
          container.style.width = width;
          container.style.height = height;
        }
      },
      postMessage: function(message) {
        const iframe = document.getElementById('${iframeId}');
        if (iframe && iframe.contentWindow) {
          iframe.contentWindow.postMessage(message, '${safeOrigin}');
        }
      }
    };

    window.addEventListener('message', function(event) {
      const data = event.data;
      if (!data || data.type !== 'serviceflow_widget_resize' || data.widgetId !== '${widgetId}') return;
      const container = document.getElementById('${containerId}');
      const iframe = document.getElementById('${iframeId}');
      if (!container || !iframe || !data.height) return;
      const nextHeight = Math.max(Number(data.height), data.modalOpen ? 920 : 640) + 'px';
      container.style.height = nextHeight;
      iframe.style.height = nextHeight;
    });
  })();
</script>`;
}
//# sourceMappingURL=widgetCatalog.js.map